import type {
    FC,
    KeyboardEvent,
    PointerEvent,
    ReactNode
} from 'react'
import type { Milestone } from '@/types'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { pad2 } from '@/lib/format'

interface MilestoneTimelineProps {
    milestones: Array<Milestone>
}

/**
 * A horizontal scrubber over the studio years, snapping to milestones. Drag, click or use the arrow keys.
 */
export const MilestoneTimeline: FC<MilestoneTimelineProps> = ({ milestones }): ReactNode => {
    const trackRef = useRef<HTMLDivElement>(null)
    const dragging = useRef(false)
    const [index, setIndex] = useState(Math.max(0, milestones.length - 1))

    if (milestones.length === 0) {
        return (
            <p className='hud border border-hairline px-4 py-10 text-center text-mute'>
                The timeline is being updated.
            </p>
        )
    }

    const firstYear = Math.min(2009, ...milestones.map(milestone => milestone.year))
    const lastYear = Math.max(2026, ...milestones.map(milestone => milestone.year))
    const years = Array.from({ length: lastYear - firstYear + 1 }, (_, offset) => firstYear + offset)
    const spanYears = Math.max(1, lastYear - firstYear)
    const current = milestones[Math.min(index, milestones.length - 1)]
    const milestoneYears = new Set(milestones.map(milestone => milestone.year))
    const position = (year: number): number => ((year - firstYear) / spanYears) * 100

    const pick = (clientX: number): void => {
        const track = trackRef.current

        if (!track) return

        const rect = track.getBoundingClientRect()
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / Math.max(1, rect.width)))
        const year = firstYear + ratio * spanYears

        let best = 0
        let bestDistance = Infinity

        milestones.forEach((milestone, candidate) => {
            const distance = Math.abs(milestone.year - year)

            if (distance < bestDistance) {
                best = candidate
                bestDistance = distance
            }
        })

        setIndex(best)
    }

    const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        dragging.current = true
        event.currentTarget.setPointerCapture(event.pointerId)
        pick(event.clientX)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        if (dragging.current) pick(event.clientX)
    }

    const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
        dragging.current = false

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const last = milestones.length - 1
        const moves: Record<string, number> = {
            ArrowLeft: index - 1,
            ArrowDown: index - 1,
            ArrowRight: index + 1,
            ArrowUp: index + 1,
            PageDown: index - 3,
            PageUp: index + 3,
            Home: 0,
            End: last
        }

        if (!(event.key in moves)) return

        event.preventDefault()
        setIndex(Math.min(last, Math.max(0, moves[event.key])))
    }

    return (
        <div>
            <div className='grid gap-8 desk:grid-cols-[minmax(0,0.85fr)_minmax(0,1.7fr)] desk:gap-16'>
                <div>
                    <p className='hud text-mute'>
                        M.{pad2(index + 1)} / {pad2(milestones.length)}
                    </p>

                    <p className='mt-4 font-heading text-[clamp(64px,9vw,128px)] font-medium leading-[0.9] tracking-[-0.04em] tabular-nums text-ink' aria-live='polite'>
                        {current.year}
                    </p>
                </div>

                <div className='self-end border-t border-hairline pt-5'>
                    <h3 className='font-heading text-[26px] font-medium leading-tight tracking-[-0.02em] text-ink desk:text-[32px]'>
                        <span data-mdlfy-cms={`milestones:${current.id}:title`}>
                            {current.title}
                        </span>
                    </h3>

                    <p className='body-copy mt-4 max-w-[52ch] text-ink/80' data-mdlfy-cms={`milestones:${current.id}:description`}>
                        {current.description}
                    </p>
                </div>
            </div>

            <div
                ref={trackRef}
                role='slider'
                tabIndex={0}
                aria-label='Studio timeline, 2009 to 2026'
                aria-valuemin={firstYear}
                aria-valuemax={lastYear}
                aria-valuenow={current.year}
                aria-valuetext={`${current.year}, ${current.title}`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
                className='relative mt-14 h-24 cursor-ew-resize touch-pan-y select-none'
            >
                <div className='absolute inset-x-0 top-10 h-px bg-hairline' />

                <div
                    className='absolute left-0 top-10 h-px origin-left bg-signal transition-[width] duration-500 ease-instrument'
                    style={{ width: `${position(current.year)}%` }}
                />

                {years.map(year => {
                    const major = milestoneYears.has(year)
                    const active = year === current.year

                    return (
                        <div key={year} className='absolute top-0 h-full' style={{ left: `${position(year)}%` }}>
                            <span
                                className={cn(
                                    'absolute left-0 w-px -translate-x-1/2',
                                    major ? 'top-6 h-4' : 'top-8 h-2',
                                    active ? 'bg-signal' : major ? 'bg-mute' : 'bg-hairline'
                                )}
                            />

                            {major && (
                                <span className={cn('absolute left-0 top-[37px] size-[7px] -translate-x-1/2', active ? 'bg-signal' : 'bg-point')} />
                            )}

                            <span
                                className={cn(
                                    'hud absolute left-0 top-14 -translate-x-1/2 text-[10px]',
                                    active ? 'text-signal' : major ? 'text-mute' : 'hidden text-mute desk:inline',
                                    !major && year % 2 === 1 && 'desk:hidden xl:inline'
                                )}
                            >
                                {String(year).slice(2)}
                            </span>
                        </div>
                    )
                })}

                <div
                    className='pointer-events-none absolute top-2 h-[64px] w-px bg-signal transition-[left] duration-500 ease-instrument'
                    style={{ left: `${position(current.year)}%` }}
                    aria-hidden='true'
                >
                    <span className='hud absolute -top-1 left-2 whitespace-nowrap text-signal'>
                        {current.year}
                    </span>
                </div>
            </div>

            <p className='hud mt-2 text-mute'>
                Drag the scrubber or use the arrow keys
            </p>
        </div>
    )
}
